
export const landingPage = {
  hero: {
    badge: "Bespire Creator Platform",
    title: "Build Your Creator Empire with AI-Powered Tools",
    description: "Bespire gives creators everything they need to build a successful online business. Create digital content, offer services, and connect with your audience—all in one place.",
    buttons: {
      getStarted: "Get Started Free",
      watchDemo: "Watch Demo"
    }
  },
  features: {
    title: "Everything You Need to Succeed",
    description: "Our platform gives you powerful tools to turn your passion into profit with multiple revenue streams.",
    items: {
      dashboard: {
        title: "Comprehensive Dashboard",
        description: "Track all your sales, subscriptions, and interactions in one intuitive dashboard."
      },
      digitalContent: {
        title: "Digital Content",
        description: "Create and sell digital products like e-books, guides, and templates to generate passive income."
      },
      consultations: {
        title: "Online Consultations",
        description: "Schedule one-on-one or group sessions with your audience at premium rates."
      },
      messaging: {
        title: "Paid Messaging",
        description: "Monetize your expertise by charging for personalized advice and direct conversations."
      },
      personalVideo: {
        title: "Personal Video Messages",
        description: "Offer custom video shoutouts and personalized content for special occasions."
      },
      analytics: {
        title: "Detailed Analytics",
        description: "Understand your audience and optimize your business with precise data insights."
      },
      aiChat: {
        title: "AI Assistant",
        description: "Get your creator business up and running quickly with our AI assistant that guides you every step of the way."
      },
      customization: {
        title: "Customization",
        description: "Tailor your profile to your brand needs with colors, layouts, and features."
      }
    }
  },
  dashboard: {
    title: "Everything in One Place, at a Glance",
    description: "The intuitive dashboard helps you track orders, revenue, and interactions with your customers.",
    welcome: "Welcome, Creator!",
    stats: {
      newOrders: "New Orders",
      lastOrder: "Last order: 2 hours ago",
      activeBookings: "Active Bookings",
      upcoming: "3 in the next 24 hours",
      unreadMessages: "Unread Messages",
      newSubscribers: "2 new subscribers today",
      revenue: "Monthly Revenue",
      growth: "+32% compared to last month"
    },
    activity: {
      title: "Your Tasks",
      viewAll: "View all",
      task1: "Upload your profile and cover images",
      task1Status: "Helps increase conversion rate",
      task2: "Create your first digital product",
      task2Status: "Passive income source",
      task3: "Set up your consultation schedule",
      task3Status: "Helps clients book appointments"
    },
    purchases: {
      title: "Recent Purchases",
      viewAll: "View all",
      customer1: "Sophie Miller",
      product1: "Workout Plan Bundle",
      amount1: "$59.99",
      date1: "Today, 1:45 PM",
      customer2: "Peter Johnson",
      product2: "1-on-1 Consultation",
      amount2: "$75.00",
      date2: "Yesterday, 9:32 AM",
      customer3: "Anna Thompson",
      product3: "Personal Video Message",
      amount3: "$39.99",
      date3: "2 days ago"
    }
  },
  aiBuilder: {
    badge: "AI-Powered Website Builder",
    title: "Design Your Perfect Creator Page with AI",
    description: "Our AI assistant guides you through creating your custom creator page with just a conversation. No technical skills required - simply describe what you want, and watch as your professional website takes shape before your eyes.",
    features: [
      "No coding required - just chat with our AI assistant",
      "Personalized page design based on your brand",
      "Content organization recommendations",
      "Smart pricing suggestions based on market data",
      "Automatic mobile optimization",
      "SEO enhancements for better discoverability"
    ],
    chat: {
      aiWelcome: "Hi! I'm your AI assistant. I'll help you build your creator page. Tell me about yourself and what kind of page you'd like to create.",
      userExample: "I'm a fitness coach and I want to create a page where I can sell workout programs and offer online consultations.",
      aiResponse: "Great! Let's create a fitness coach page for you. What style would you prefer: modern and energetic, or clean and professional?",
      inputPlaceholder: "Type your message..."
    }
  },
  testimonials: {
    title: "Loved by Creators",
    description: "Join thousands of creators who are growing their businesses with our platform.",
    items: [
      {
        content: "Bespire transformed my creator business. The AI builder made it so easy to set up my page, and the multiple income streams have increased my revenue by 70%.",
        author: "Sarah Jensen",
        role: "Fitness Creator"
      },
      {
        content: "I was skeptical at first, but Bespire truly delivers. The analytics dashboard helps me understand what content sells best, and the consultation booking system is seamless.",
        author: "Mark Thompson",
        role: "Financial Coach"
      },
      {
        content: "The personal video messages feature has become my biggest income source. Fans love the personal connection, and I love how easy Bespire makes it to manage orders.",
        author: "Elena Rodriguez",
        role: "Content Creator"
      }
    ]
  },
  pricing: {
    title: "Simple and Transparent Pricing",
    description: "No monthly fees or hidden charges. You only pay when you earn.",
    free: {
      title: "Free Plan",
      description: "Perfect for getting started",
      price: "$0",
      period: "/month",
      button: "Start Free",
      includedTitle: "What's included:",
      features: [
        "Basic creator profile",
        "Digital content sales (10% commission)",
        "Standard analytics",
        "Email support"
      ]
    },
    pro: {
      title: "Pro Plan",
      description: "For serious creators",
      price: "5%",
      period: "commission only",
      button: "Upgrade to Pro",
      includedTitle: "Everything in Free, plus:",
      features: [
        "Custom domain name",
        "Lower 5% commission rate",
        "Priority payment processing",
        "Advanced analytics",
        "Priority support",
        "Custom branding options"
      ],
      badge: "POPULAR"
    }
  },
  faq: {
    title: "Frequently Asked Questions",
    description: "Have more questions? Contact our support team.",
    items: [
      {
        question: "How does the AI website builder work?",
        answer: "Our AI website builder starts with a short onboarding process where creators provide basic information. Then, the AI asks targeted questions to fully customize the page according to the creator's specific needs. After the page is created, creators can continuously use the AI assistant to edit and maintain their site, making any changes easily without technical knowledge."
      },
      {
        question: "What types of digital content can I sell?",
        answer: "You can sell virtually any type of digital content, including e-books, video tutorials, online courses, workout plans, meal plans, templates, artwork, music, and more. Our platform supports various file formats to accommodate different content types."
      },
      {
        question: "How do payments work?",
        answer: "We handle all payment processing for you. When someone purchases your content or services, the payment is securely processed, and you receive the funds in your account after our commission. You can then withdraw your earnings to your bank account at any time."
      },
      {
        question: "Can I offer subscription-based content?",
        answer: "Yes, you can create subscription-based offerings where fans pay a recurring fee for exclusive content, updates, or ongoing services. This helps you build a stable, predictable income stream."
      },
      {
        question: "How do I schedule consultations with clients?",
        answer: "Our platform includes an integrated scheduling system. You set your availability, and clients can book time slots directly through your page. You'll receive notifications for new bookings, and clients get automatic reminders before the session."
      }
    ]
  },
  newsletter: {
    title: "Be the First to Know When We Launch",
    description: "Subscribe to our newsletter to get early access!",
    placeholder: "Enter your email address",
    button: "Subscribe",
    success: {
      title: "Thank You for Subscribing!",
      description: "We'll keep you updated on our launch and send you valuable creator resources."
    },
    privacy: "We respect your privacy. Unsubscribe at any time."
  },
  footer: {
    description: "The all-in-one platform for creators to build, grow, and monetize their online presence with AI-powered tools.",
    categories: {
      platform: "Platform",
      support: "Support",
      legal: "Legal"
    },
    platformLinks: [
      "Features",
      "Pricing",
      "AI Builder",
      "Testimonials"
    ],
    supportLinks: [
      "Help Center",
      "Creator Guides",
      "Contact Us",
      "FAQ"
    ],
    legalLinks: [
      "Privacy Policy",
      "Terms of Service",
      "Cookie Policy",
      "Acceptable Use"
    ],
    copyright: "© {year} Bespire. All rights reserved.",
    bottomLinks: [
      "Privacy",
      "Terms",
      "Cookies"
    ]
  },
  communitySupport: {
    badge: "Community Support",
    title: "You'll Never Be Alone on Your Journey",
    description: "On the Bespire platform, you not only get tools but also a supportive community and expert help to become a successful alkotói",
    features: [
      {
        title: "Alkotói Community",
        description: "Join other successful alkotóis, share ideas and build valuable connections"
      },
      {
        title: "Creative Inspiration",
        description: "Discover successful strategies and new ideas based on examples from other alkotóis"
      },
      {
        title: "24/7 Support",
        description: "Get help anytime from our dedicated support team who will assist you with everything"
      },
      {
        title: "Detailed Knowledge Base",
        description: "Educational materials, guides and examples help you get the most out of our platform"
      }
    ],
    quote: "Successful alkotóis never work completely alone. The power of community and proper support is what sets them apart.",
    joinText: "Join more than 10,000 alkotóis"
  },
  contentTools: {
    badge: "Content Creation Tools",
    title: "Creating Content Has Never Been Easier",
    description: "Create professional-quality digital content in minutes with our powerful tools",
    tools: [
      {
        title: "Digital Content Made Simple",
        description: "Create quality digital products with our drag-and-drop editor, no special technical knowledge required"
      },
      {
        title: "One-Click Publishing",
        description: "Save and publish your content instantly, without complicated processes"
      },
      {
        title: "Rapid Content Updates",
        description: "Update your content anytime, changes go live immediately, and buyers are automatically notified"
      },
      {
        title: "Ratings and Feedback",
        description: "Collect ratings and feedback from buyers to continuously improve your content"
      }
    ]
  },
  creatorSecurity: {
    badge: "Reliability and Security",
    title: "We Protect Your Data and Your Clients' Data",
    description: "Our platform protects all your data and revenue with advanced security measures so you can focus on content creation",
    features: [
      {
        title: "Secure Payment Processing",
        description: "We process all transactions through secure payment gateways, complying with the strictest industry standards."
      },
      {
        title: "Content Protection",
        description: "Your digital products are protected with DRM to prevent unauthorized access and sharing."
      },
      {
        title: "Data Protection",
        description: "Our GDPR-compliant system ensures that personal data is always handled in accordance with regulations."
      },
      {
        title: "Transparent Commission System",
        description: "No hidden costs or unexpected deductions - you always know exactly how much you earn and when you get it."
      }
    ],
    security: {
      payment: "Secure Payment",
      privacy: "Data Protection",
      encryption: "SSL Encryption"
    }
  }
};
